import Axios from 'helpers/Interceptor'
import { AuthError } from 'Hooks/useUser';
import { AttributionDetails, RewardfulDetails, UserData } from 'modules/Types';

/**
 * Updates user metadata with tracking and attribution information
 * @async
 * @param {string} userId - The ID of the user to update
 * @param {Object} metadata - The metadata to add or update
 * @param {string} [metadata.affiliate_referrer] - Partner/affiliate referral code
 * @param {AttributionDetails} [metadata.attribution] - Initial UTM attribution parameters
 * @param {AttributionDetails} [metadata.last_touch_attribution] - Most recent UTM parameters
 * @param {RewardfulDetails} [metadata.rewardful] - Rewardful referral tracking data
 * @returns {Promise<UserResponse>} Updated user data from the server
 */
export default async function updateUserMetadata_api(userId: string, metadata: {
    affiliate_referrer?: string;
    attribution?: AttributionDetails
    last_touch_attribution?: AttributionDetails
    rewardful?: RewardfulDetails
}): Promise<UserData> {
    const response = await Axios.post<UserData>(`/user/${userId}/add-metadata`, {
        ...metadata,
    })
    return response.data
}

/** Helper function to get user by Auth0 ID */
export const getUserByAuth0Id_api = async (userSubAuth0Id: string): Promise<UserData> => {
    try {
        const res = await Axios.get<UserData>(`/user/${userSubAuth0Id}`);
        if (!res.data) {
            throw new AuthError('Error fetching user data');
        }
        return res.data;
    } catch (error) {
        throw new AuthError(`Failed to get user: ${error}`);
    }
};

/** Gets current user data and validates Facebook token if needed */
export const getUser_api = async (): Promise<{ facebook: any }> => {
    try {
        const userData = await Axios.get<{ facebook: any }>('/user/me');
        return userData.data;
    } catch (error) {
        throw new AuthError('Failed to get current user data');
    }
};

/** Gets all users */
export const getUsers_api = async (payload: { email: string }): Promise<UserData[]> => {
    const response = await Axios.post<UserData[]>('/user/search/users', payload);
    return response.data;
}

export const updateUser_api = async (userId: string, payload: any) => {
    try {
        const response = await Axios.put<UserData>(`/user/${userId}`, payload);
        return response.data;
    } catch (error) {
        throw error;
    }
}
/** Interface for affiliate data from the API */
interface AffiliateData {
    /** Rewardful affiliate link for the user */
    rewardful_affiliate_link: string;
}

/** 
 * Gets the user's full affiliate data including referral link
 * @returns Promise containing affiliate data or null if not found
 * @throws Error if request fails
 */
export const getFullAffiliateData_api = async (): Promise<AffiliateData | null> => {
    try {
        const response = await Axios.get<AffiliateData>('/user/me/affiliate');
        return response.data;
    } catch (error) {
        console.error('Error fetching affiliate details:', error);
        throw error;
    }
};

/** Opens the Rewardful affiliate dashboard for the current user */
export const openRewardfulDashboard_api = async (): Promise<void> => {
    try {
        const response = await Axios.get<string>('/user/me/affiliate/dashboard');
        if (response.data) {
            window.location.href = response.data
        } else {
            throw new Error('Failed to retrieve dashboard link');
        }
    } catch (error) {
        console.error('Error opening Rewardful dashboard:', error);
        throw error;
    }
};

/** 
 * Connects a user's social media account
 * @param {Object} connectionData - Data needed to connect the account
 * @returns {Promise<void>}
 */
export const connectUserAccount_api = async (connectionData: Record<string, any>): Promise<void> => {
    await Axios.post('/user/me/connect', connectionData);
};

/** Interface for email addition payload */
interface AddEmailPayload {
    /** Email address to add */
    email: string;
    /** Optional name of the user */
    name?: string;
}

/** Interface for email addition response */
interface AddEmailResponse {
    success: boolean;
    message: string;
}

/** 
 * Adds an email address to the user's account
 * @param {AddEmailPayload} payload - Email and optional name
 * @returns {Promise<AddEmailResponse>} Response data from the server
 * @throws {Error} If the request fails
 */
export const updateUserEmail_api = async (payload: AddEmailPayload): Promise<AddEmailResponse> => {
    try {
        const response = await Axios.post<AddEmailResponse>('/user/add-email', payload);
        return response.data;
    } catch (error) {
        console.error('Error adding user email:', error);
        throw error;
    }
};

export const deleteUser_api = async (userId: number): Promise<void> => {
    try {
        await Axios.delete(`/user/${userId}`);
    } catch (error) {
        console.error('Error deleting user:', error);
        throw error;
    }
};

/** Interface for user invoice filters */
interface UserInvoiceFilters {
    /** Filter by brand ID */
    brandId?: string;
    /** Filter by brand name */
    brandName?: string;
    /** Start date for filtering invoices (YYYY-MM-DD) */
    startDate?: string;
    /** End date for filtering invoices (YYYY-MM-DD) */
    endDate?: string;
}

/** Interface for user invoice response */
export interface UserInvoice {
    id: string;
    amount_due: number;
    amount_paid: number;
    amount_remaining: number;
    currency: string;
    status: string;
    due_date: string | null;
    created: number;
    invoice_pdf?: string;
    hosted_invoice_url?: string;
    payment_description?: string;
    payment_name?: string;
    /** Brand information */
    brand_id?: string;
    brand_name?: string;
}

/** 
 * Gets all invoices for the current user across all their brands
 * @param {UserInvoiceFilters} filters - Optional filters to apply
 * @returns {Promise<UserInvoice[]>} Array of user invoices
 * @throws {Error} If the request fails
 */
export const getUserInvoices_api = async (filters?: UserInvoiceFilters): Promise<UserInvoice[]> => {
    try {
        const params = new URLSearchParams();
        if (filters?.brandId) params.append('brandId', filters.brandId);
        if (filters?.brandName) params.append('brandName', filters.brandName);
        if (filters?.startDate) params.append('startDate', filters.startDate);
        if (filters?.endDate) params.append('endDate', filters.endDate);

        const queryString = params.toString();
        const url = `/user/me/invoices${queryString ? `?${queryString}` : ''}`;
        
        const response = await Axios.get<UserInvoice[]>(url);
        return response.data;
    } catch (error) {
        console.error('Error fetching user invoices:', error);
        throw error;
    }
};